// Swym assigns `window._swat` -- with `initializeActionButtons` already on it --
// roughly 100ms before the app finishes booting, and calling it inside that
// window throws from Swym's own internals. It exposes no readiness flag to test
// for, so the only way to tell "ready" from "present" is to call it and see.
const RETRY_LIMIT = 20;
const RETRY_DELAY = 100;

/**
 * Re-scan a DOM subtree for Swym Wishlist Plus `[data-swaction]` buttons after
 * dynamic HTML updates. See https://developers.getswym.com/docs/swym-powered-buttons
 *
 * @param {Element} [scope=document.body] - Container that includes Swym buttons
 */
export default function reinitSwymActionButtons(scope = document.body) {
  if (!scope) {
    return;
  }

  let attempts = 0;

  // Kept out of `run`'s signature deliberately: Swym invokes SwymCallbacks
  // entries with arguments of its own, which would clobber a counter parameter.
  const run = () => {
    const swat = window._swat;
    if (!swat || typeof swat.initializeActionButtons !== 'function') {
      return;
    }

    let containerSelector = 'body';
    let cleanup;

    if (scope !== document.body && scope !== document.documentElement) {
      if (scope.id) {
        containerSelector = `#${CSS.escape(scope.id)}`;
      } else {
        const token = `swym-reinit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        scope.setAttribute('data-swym-reinit-scope', token);
        containerSelector = `[data-swym-reinit-scope="${CSS.escape(token)}"]`;
        cleanup = () => scope.removeAttribute('data-swym-reinit-scope');
      }
    }

    try {
      swat.initializeActionButtons(containerSelector);
    } catch {
      // Swym is present but not booted yet. Retry on a fresh scope token --
      // `cleanup` below has already removed this attempt's.
      if (attempts < RETRY_LIMIT) {
        attempts += 1;
        setTimeout(run, RETRY_DELAY);
      }
    } finally {
      cleanup?.();
    }
  };

  if (
    window._swat &&
    typeof window._swat.initializeActionButtons === 'function'
  ) {
    run();
  } else {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(run);
  }
}
